import type { AnySceneObjectT } from "tweenly"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded"
import LayersIcon from "@mui/icons-material/Layers"
import LayersClearIcon from "@mui/icons-material/LayersClear"
import React from "react"
import { isWindows } from "react-device-detect"
import { useTranslation } from "react-i18next"
import { selectUndoable } from "../../../helpers/selector.helpers"
import useMultiselectObject from "../../../hooks/editor/useMultiselectObject"
import useMultiselectObjectAction from "../../../hooks/editor/useMultiselectObjectAction"
import { useObjectActions } from "../../../hooks/useObjectActions"
import { useAppDispatch, useAppSelector } from "../../../hooks/useRedux"
import {
    selectHasMultiSelection,
    selectIsObjectSelected,
} from "../../../store/slices/activeObject.slice"
import {
    duplicateObjects,
    selectObjectById,
    unGroupObjectsAction,
    selectObjectIdsWithStaticDescendant,
} from "../../../store/slices/objects.slice"
import RightClickContextMenu, { MenuItemT } from "../../common/ContextMenu/RightClickContextMenu"
import AutoFrameContainer from "./CanvasContainerType/AutoFrameContainer"
import FrameContainer from "./CanvasContainerType/FrameContainer"
import IconContainer from "./CanvasContainerType/IconContainer"
import ImageContainer from "./CanvasContainerType/ImageContainer"
import ItemContainer from "./CanvasContainerType/ItemContainer"
import LineContainer from "./CanvasContainerType/LineContainer"
import SequenceContainer from "./CanvasContainerType/SequenceContainer"
import SvgContainer from "./CanvasContainerType/SvgContainer"
import TextContainer from "./CanvasContainerType/TextContainer"
import VideoPanelContainer from "./CanvasContainerType/VideoPanelContainer"

interface Props {
    object: AnySceneObjectT
    canvasScaleCoef: number
    isHiding: boolean //hiding objects where parent isHidden
    isLocking: boolean //locking objects where parent isLocked
    isRootTicker: boolean //disable click or modified objects where parentId is ticker
    borderCoef: number
}

export type ObjectType = {
    position: "relative" | "absolute"
    left: number
    top: number
    width: number
    height: number
}

/**
 * memo: Canvas re-renders whenever the selection changes, but every prop it passes down here
 * is referentially stable, so all the untouched items can bail out instead of re-rendering
 * their whole subtree.
 */
const CanvasItem = React.memo(
    ({ object, canvasScaleCoef, isHiding, isLocking, isRootTicker, borderCoef }: Props) => {
        const { t } = useTranslation()
        const dispatch = useAppDispatch()
        // a boolean, not the selection array — switching between single selections then leaves all
        // the other canvas items untouched
        const hasMultiSelection = useAppSelector(selectHasMultiSelection)
        // both of these used to come from a subscription to the whole objects slice, which
        // re-rendered all ~200 canvas items on every object change. Booleans are referentially
        // stable, so now only the items whose answer actually flips re-render.
        const isParentTicker = useAppSelector(
            (state) =>
                selectObjectById(selectUndoable(state).objects, object.parentId)?.type === "ticker"
        )
        const hasStaticChild = useAppSelector((state) =>
            selectObjectIdsWithStaticDescendant(selectUndoable(state).objects).has(object.id)
        )

        // whether THIS object is one of the objects a multiselection is made of
        const isSelected = useAppSelector((state) => selectIsObjectSelected(state, object.id))
        // one locked-down member is enough to make wrapping the group wrong
        const isGroupWrapDisabled = useAppSelector((state) =>
            state.activeObject.selected.some((id) =>
                selectObjectIdsWithStaticDescendant(selectUndoable(state).objects).has(id)
            )
        )

        const { handleMultiselectObject } = useMultiselectObject(object)
        const { handleGroupObjects, handleDuplicateObjects, handleDeleteObjects } =
            useMultiselectObjectAction()
        const { deleteObject } = useObjectActions()

        // a function, not an array: every item holds an MUI SVG icon element and there is one
        // CanvasItem per scene object, so this used to build ~800 React elements per canvas
        // render for a menu that is almost never open
        const getMenuItems = (): MenuItemT[] => [
            {
                icon: <LayersClearIcon />,
                title: t("editor:unWrap"),
                shortCut: isWindows ? "Ctrl Alt G" : "⌥ ⌘ G",
                onClick: (event) => handleUnGroupObjects(event, object),
                hidden: !object.isGroup,
            },
            {
                icon: <LayersIcon />,
                title: t("editor:wrap"),
                shortCut: isWindows ? "Ctrl G" : "⌘ G",
                onClick: (event) => handleGroupObject(event),
                disabled: hasStaticChild,
            },
            {
                icon: <ContentCopyIcon />,
                title: t("editor:duplicate"),
                shortCut: isWindows ? "Ctrl D" : "⌘ D",
                onClick: (event) => handleDuplicateObject(event, object),
                divider: true,
            },
            {
                icon: <DeleteRoundedIcon />,
                title: t("editor:delete"),
                shortCut: isWindows ? "Ctrl Del" : "⌘ ⌫",
                onClick: (event) => handleDeleteObject(event, object),
                color: "error",
            },
        ]
        const stopEvent = (event: React.MouseEvent) => {
            event.preventDefault()
            event.cancelable = true
            if (event.stopPropagation) event.stopPropagation()
        }

        /**
         * Menu for the whole multiselection.
         *
         * It used to hang off `ObjectsWrapper`, which only worked because that component
         * re-rendered the selected objects inside itself. Now that they stay where they
         * belong, the right-click has to be answered by the member that was clicked.
         */
        const getMultiSelectionMenuItems = (): MenuItemT[] => [
            {
                icon: <LayersIcon />,
                title: t("editor:wrapSelected"),
                shortCut: isWindows ? "Ctrl G" : "⌘ G",
                onClick: (event) => {
                    stopEvent(event)
                    handleGroupObjects()
                },
                disabled: isGroupWrapDisabled,
            },
            {
                icon: <ContentCopyIcon />,
                title: t("editor:duplicateSelected"),
                shortCut: isWindows ? "Ctrl D" : "⌘ D",
                onClick: (event) => {
                    stopEvent(event)
                    handleDuplicateObjects()
                },
                divider: true,
            },
            {
                icon: <DeleteRoundedIcon />,
                title: t("editor:deleteSelected"),
                shortCut: isWindows ? "Ctrl Del" : "⌘ ⌫",
                onClick: handleDeleteObjects,
                color: "error",
            },
        ]

        const getElementaryItem = () => {
            switch (object.type) {
                case "frame":
                    return (
                        <FrameContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                case "item":
                case "ellipse":
                case "ticker":
                    return (
                        <ItemContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                case "line":
                    return (
                        <LineContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                case "auto-frame":
                    return (
                        <AutoFrameContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                case "flex-frame":
                    return (
                        <AutoFrameContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                case "video-panel":
                    return (
                        <VideoPanelContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                case "image":
                    return (
                        <ImageContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                case "text":
                    return (
                        <TextContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                case "sequence":
                    return (
                        <SequenceContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                case "svg":
                    return (
                        <SvgContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                case "icon":
                    return (
                        <IconContainer
                            object={object}
                            isHiding={isHiding}
                            isLocking={isLocking}
                            isRootTicker={isRootTicker || isParentTicker}
                            canvasScaleCoef={canvasScaleCoef}
                            borderCoef={borderCoef}
                            handleSelectObject={handleSelectObject}
                        />
                    )
                default:
                    return <></>
            }
        }

        if (!object) {
            return <React.Fragment></React.Fragment>
        }

        const handleSelectObject = (
            event: React.MouseEvent,
            objectId: string,
            animationIds: string[],
            multiSelectKey: boolean
        ) => {
            event.preventDefault()
            event.cancelable = true
            if (event.stopPropagation) event.stopPropagation()
            handleMultiselectObject(multiSelectKey, isRootTicker)
        }
        const handleUnGroupObjects = (event: React.MouseEvent, object: AnySceneObjectT) => {
            event.preventDefault()
            event.cancelable = true
            if (event.stopPropagation) event.stopPropagation()

            dispatch(unGroupObjectsAction({ object }))
        }
        const handleGroupObject = (event: React.MouseEvent) => {
            event.preventDefault()
            event.cancelable = true
            if (event.stopPropagation) event.stopPropagation()

            handleGroupObjects()
        }

        const handleDuplicateObject = (event: React.MouseEvent, object: AnySceneObjectT) => {
            event.preventDefault()
            event.cancelable = true
            if (event.stopPropagation) event.stopPropagation()

            dispatch(duplicateObjects([object]))
        }
        const handleDeleteObject = (event: React.MouseEvent, object: AnySceneObjectT) => {
            event.preventDefault()
            event.cancelable = true
            if (event.stopPropagation) event.stopPropagation()

            deleteObject(object)
        }

        // no context menu for something the user cannot act on anyway
        const canRightClick = !isLocking && !object.isLocked && !(isParentTicker || isRootTicker)

        if (!canRightClick) return getElementaryItem()

        /**
         * A right click on a member of a multiselection acts on the WHOLE selection; on
         * anything else it acts on that one object. Previously the group menu lived on
         * `ObjectsWrapper` and every canvas item simply gave its menu up while a
         * multiselection was active, which left a right click on a non-member doing nothing.
         */
        const isMultiSelectionMember = hasMultiSelection && isSelected

        /**
         * Right-clicking something outside the selection makes it the selection first.
         *
         * Without this the menu and the thing it acts on can disagree: "Wrap" goes through
         * `groupSelectedObjectsAction`, which reads the selection, while "Duplicate" and
         * "Delete" take this object — so right-clicking a non-member while two other
         * objects were selected offered a single-object menu that wrapped the other two.
         */
        const handleBeforeOpen = () => {
            if (!isSelected) handleMultiselectObject(false, isRootTicker)
        }

        return (
            <RightClickContextMenu
                key={object.id}
                scaleCoef={canvasScaleCoef}
                menuLabel={getElementaryItem()}
                onBeforeOpen={handleBeforeOpen}
                data={isMultiSelectionMember ? getMultiSelectionMenuItems : getMenuItems}
            />
        )
    }
)

export default CanvasItem
